'use client';

import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

import { readScrollEdges } from 'src/components/cmui/scroll-row/scroll-edges';

/**
 * 옆으로 구르는 통의 **「오른쪽에 아직 남았다」 표시** — 표(`HubTable`)와 폰의 칩 줄(`HubConditionBar`)이 함께 쓴다.
 *
 * ⚠️ 표시가 없으면 폰에서 마지막 칸이 중간에서 잘린 채로만 보여 「깨졌다」로 읽힌다.
 * 🔑 값을 상태로 드는 까닭은 **끝까지 밀면 사라져야** 해서다 — 늘 떠 있으면 다 본 뒤에도 뭔가 더 있는 것처럼 보인다.
 */

/**
 * 구르는 통에 붙일 `ref` · `onScroll` 과 「오른쪽이 남았나」.
 * @param deps 바뀌면 다시 잰다(줄 수·칩 수처럼 가로폭을 바꾸는 것)
 * @returns `ref` 는 구르는 통에, `onScroll` 은 그 통의 스크롤에 건다
 */
export function useHubScrollEdge(deps: unknown) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hasMoreRight, setHasMoreRight] = useState(false);

  /* 끝까지 밀었는지 잰다(`readScrollEdges` — 1px 반올림 여유) */
  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setHasMoreRight(readScrollEdges(el).end);
  }, []);

  /*
    ⚠️ 첫 그림 뒤에 한 번 재고, 통·내용의 폭이 바뀌어도 다시 잰다(`ResizeObserver`) —
      스크롤만 보면 처음 상태를 놓친다.
  */
  useEffect(() => {
    onScroll();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [onScroll, deps]);

  return { ref, onScroll, hasMoreRight };
}

/**
 * 오른쪽 끝의 옅은 띠 — 구르는 통의 **형제**로, `position: relative` 인 부모 안에 둔다.
 * ⚠️ `pointerEvents: 'none'` 이라 밑의 마지막 칸·칩이 그대로 눌린다.
 */
export function HubScrollFade({
  show,
  width = 36,
  surface = 'paper',
}: {
  show: boolean;
  width?: number;
  /** 띠가 녹아드는 바탕 — 카드 안이면 `paper`, 화면 바탕에 바로 서면 `default` */
  surface?: 'paper' | 'default';
}) {
  if (!show) return null;
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute', top: 0, bottom: 0, right: 0, width, zIndex: 1,
        pointerEvents: 'none',
        background: (theme) => `linear-gradient(to right, ${alpha(theme.palette.background[surface], 0)}, ${theme.palette.background[surface]})`,
      }}
    />
  );
}
